import React from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ServiceNavigationProps {
  currentService?: string;
}

const ServiceNavigation: React.FC<ServiceNavigationProps> = ({ currentService }) => {
  const location = useLocation();
  
  const services = [
    {
      id: 'catering',
      title: 'Catering Marino',
      route: '/servicios/catering',
      color: 'ocean'
    },
    {
      id: 'eventos',
      title: 'Eventos Privados',
      route: '/servicios/eventos',
      color: 'jade'
    },
    {
      id: 'menu-degustacion',
      title: 'Menú Degustación',
      route: '/servicios/menu-degustacion',
      color: 'seagreen'
    },
    {
      id: 'consultoria',
      title: 'Consultoría Gastronómica',
      route: '/servicios/consultoria',
      color: 'coral'
    }
  ];

  // Encuentra el índice del servicio actual
  const currentIndex = services.findIndex(service => 
    location.pathname.includes(service.id) || service.id === currentService
  );
  
  const prevService = currentIndex > 0 ? services[currentIndex - 1] : null;
  const nextService = currentIndex < services.length - 1 ? services[currentIndex + 1] : null;

  const colorClasses = {
    ocean: 'from-ocean-500 to-ocean-600 hover:from-ocean-600 hover:to-ocean-700',
    jade: 'from-jade-500 to-jade-600 hover:from-jade-600 hover:to-jade-700',
    seagreen: 'from-seagreen-500 to-seagreen-600 hover:from-seagreen-600 hover:to-seagreen-700',
    coral: 'from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700'
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Navegación principal */}
      <motion.div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Botón volver a servicios */}
        <Link 
          to="/servicios" 
          className="inline-flex items-center text-ocean-600 hover:text-ocean-700 font-semibold transition-colors duration-200 group"
        >
          <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
          Todos los Servicios
        </Link>

        {/* Navegación entre servicios */}
        <div className="flex items-center gap-3">
          {/* Servicio anterior */}
          {prevService && (
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link to={prevService.route}>
                <Button
                  variant="outline"
                  size="sm"
                  className={`bg-gradient-to-r ${colorClasses[prevService.color as keyof typeof colorClasses]} text-white border-0 shadow-md hover:shadow-lg transition-all duration-300 px-4 py-2 rounded-full`}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">{prevService.title}</span>
                  <span className="sm:hidden">Anterior</span>
                </Button>
              </Link>
            </motion.div>
          )}

          {/* Indicador de posición */}
          <div className="flex items-center gap-1">
            {services.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'bg-ocean-500 w-6' 
                    : 'bg-silver-300'
                }`}
              />
            ))}
          </div>

          {/* Servicio siguiente */}
          {nextService && (
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link to={nextService.route}>
                <Button
                  variant="outline"
                  size="sm"
                  className={`bg-gradient-to-r ${colorClasses[nextService.color as keyof typeof colorClasses]} text-white border-0 shadow-md hover:shadow-lg transition-all duration-300 px-4 py-2 rounded-full`}
                >
                  <span className="hidden sm:inline">{nextService.title}</span>
                  <span className="sm:hidden">Siguiente</span>
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Navegación rápida - Solo en desktop */}
      <motion.div
        className="hidden lg:block border-t border-silver-200 pt-6"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <h3 className="text-sm font-semibold text-deep-600 mb-3">Explorar otros servicios:</h3>
        <div className="flex flex-wrap gap-2">
          {services.map((service, index) => (
            <Link 
              key={service.id}
              to={service.route}
              className={`relative ${index === currentIndex ? 'pointer-events-none' : ''}`}
            >
              <motion.div
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  index === currentIndex 
                    ? `bg-gradient-to-r ${colorClasses[service.color as keyof typeof colorClasses]} text-white shadow-md`
                    : 'bg-silver-100 text-deep-600 hover:bg-silver-200'
                }`}
                whileHover={index !== currentIndex ? { scale: 1.05, y: -2 } : {}}
                whileTap={index !== currentIndex ? { scale: 0.95 } : {}}
              >
                {service.title}
                {index === currentIndex && (
                  <motion.div
                    className="absolute -top-1 -right-1 w-3 h-3 bg-jade-400 rounded-full border-2 border-white"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  />
                )}
              </motion.div>
            </Link>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default ServiceNavigation;
